<template>
  <div>
    <Banner heading="Manage app updates" v-if="$vuetify.breakpoint.mdAndUp" style="height: 120px" class="ma-0"/>
    <v-container class="pb-12">
      <v-card
        class="mx-md-12 mt-md-6 mb-3"
        :flat="$vuetify.breakpoint.smAndDown"
      >
        <v-form ref="form" v-model="isValid">
          <div class="px-10 pt-12 pb-0">
            <div class="mb-3">
              <span class="title">Update versions</span><span class="body-1 ml-2">| {{selectedApp.name}}</span>
            </div>
            <v-row class="pb-3">
              <v-col cols="12" md="6" class="pr-3"
                :style="{'border-right': $vuetify.breakpoint.mdAndUp ? '1px solid #E4E4E6' : '',
                        'border-bottom': $vuetify.breakpoint.smAndDown ? '1px solid #E4E4E6' : ''}">
                <div class="d-flex align-center">
                  <v-icon class="mb-1">mdi-apple</v-icon>
                  <span class="title ml-2">iOS</span>
                </div>
                <div class="mt-6">
                  <v-text-field
                    outlined
                    v-model="iosData.criticalVersion"
                    label="Force updates to these versions"
                    placeholder=" "
                    class="mb-3"
                    :rules="[rules.required, rules.versionNumber]"
                  >
                  </v-text-field>

                  <v-text-field
                    outlined
                    v-model="iosData.stableVersion"
                    label="Recommend updates to these versions"
                    placeholder=" "
                    class="mb-3"
                    :rules="[rules.required, rules.versionNumber]"
                  >
                  </v-text-field>

                  <v-text-field
                    outlined
                    v-model="iosData.appUpdateLink"
                    label="App Store link"
                    placeholder=" "
                    :rules="[rules.required, rules.url]"
                  >
                  </v-text-field>
                </div>
              </v-col>
              <v-col cols="12" md="6" class="pl-3">
                <div class="d-flex align-center">
                  <v-icon class="mb-1">android</v-icon>
                  <span class="title ml-2">Android</span>
                </div>
                <div class="mt-6">
                  <v-text-field
                    outlined
                    v-model="androidData.criticalVersion"
                    label="Force updates to these versions"
                    placeholder=" "
                    class="mb-3"
                    :rules="[rules.required, rules.versionNumber]"
                  >
                  </v-text-field>

                  <v-text-field
                    outlined
                    v-model="androidData.stableVersion"
                    label="Recommend updates to these versions"
                    placeholder=" "
                    class="mb-3"
                    :rules="[rules.required, rules.versionNumber]"
                  >
                  </v-text-field>

                  <v-text-field
                    outlined
                    v-model="androidData.appUpdateLink"
                    label="Play Store link"
                    placeholder=" "
                    :rules="[rules.required, rules.url]"
                  >
                  </v-text-field>
                </div>
              </v-col>
            </v-row>
          </div>

          <v-divider />

          <div class="pa-10 pb-6">
            <div class="mb-6">
              <span class="title">Update message</span>
            </div>
            <div>
              <v-text-field
                outlined
                v-model="forcedUpdateMessage"
                label="Forced update message"
                placeholder=" "
                class="mb-3"
                :rules="[rules.required]"
              >
              </v-text-field>
            </div>
            <div>
              <v-text-field
                outlined
                v-model="suggestedUpdateMessage"
                label="Recommended update message"
                placeholder=" "
                :rules="[rules.required]"
              >
              </v-text-field>
            </div>
          </div>
        </v-form>
      </v-card>
      <div class="d-flex justify-end mr-12">
        <!--<v-btn
          text
          class="mr-6"
          @click="$router.go(-1)"
        >Cancel</v-btn>-->
        <v-btn
          class="primary"
          width="120px"
          @click="update"
          :disabled="!isValid"
        >Update</v-btn>
      </div>
    </v-container>
  </div>
</template>

<script>
  import {Banner} from '@nswdoe/doe-ui-core';
  import { mapGetters } from 'vuex';
  import { getConfig } from '@/utils/config';

  export default {
    name: 'app-update',
    components: {
      Banner
    },
    data() {
      // this is the operating system key that has to match to what the apps are querying
      const iOSKey = getConfig('VUE_APP_IOS_OS_KEY');
      const androidKey = getConfig('VUE_APP_ANDROID_OS_KEY');
      return {
        iOSKey,
        androidKey,
        iosData: {},
        androidData: {},
        forcedUpdateMessage: null,
        suggestedUpdateMessage: null,
        isValid: false,
        rules: {
          required: val => val != null && val.trim() !== '' || 'Required.',
          versionNumber: val => /^[0-9].*[a-zA-Z0-9]$/.test(val) || 'Version must start with a number and end with an alphanumeric.',
          url: val => /^http[s]?:\/\/.*\..*[a-zA-Z0-9/]$/.test(val) || 'Link must be a url.'
        }
      }
    },
    computed: {
      ...mapGetters({
        selectedApp: 'selectedApp'
      }),
      appVersions() {
        return this.$store.getters.appVersions(this.selectedApp.code);
      },
      originalIosData() {
        return this.appVersions[this.iOSKey] || {};
      },
      originalAndroidData() {
        return this.appVersions[this.androidKey] || {};
      }
    },
    methods: {
      formatIsoDateTimeNoMilliseconds(date) {
        // this api wants the date in this ISO/UTC format: "2020-09-01T05:15:49Z" (no milliseconds)
        const isoDateStr = date.toISOString();
        return isoDateStr.split('.')[0] + "Z";
      },
      updateOs(osData) {
        const enableDate = new Date();

        const data = {
          appCode: this.selectedApp.code,
          os: osData.os,
          stableVersion: osData.stableVersion,
          criticalVersion: osData.criticalVersion,
          appUpdateLink: osData.appUpdateLink,
          criticalUpdateMessage: this.forcedUpdateMessage,
          criticalUpdateDatetime: this.formatIsoDateTimeNoMilliseconds(enableDate),
          suggestedUpdateMessage: this.suggestedUpdateMessage,
          suggestedUpdateDatetime: this.formatIsoDateTimeNoMilliseconds(enableDate),
        };

        return this.$store.dispatch('updateAppVersion', data);
      },
      update() {
        if (this.$refs.form.validate()) {
          Promise.all([
            this.updateOs(this.iosData),
            this.updateOs(this.androidData)])
          .catch(err => {
            console.error(err); // eslint-disable-line no-console
          });
        }
      }
    },
    created() {
      this.$store.dispatch('fetchAppVersions', {appId: 'StaffPortalApp', os: this.iOSKey})
        .then(() => {
          const appKeys = {appName: this.originalIosData.appName || 'StaffPortalApp', os: this.originalIosData.os || this.iOSKey};
          this.iosData = Object.assign({}, this.originalIosData, appKeys);
          this.forcedUpdateMessage = this.iosData.criticalUpdateMessage;
          this.suggestedUpdateMessage = this.iosData.suggestedUpdateMessage;
        });

      this.$store.dispatch('fetchAppVersions', {appId: 'StaffPortalApp', os: this.androidKey})
        .then(() => {
          const appKeys = {appName: this.originalAndroidData.appName || 'StaffPortalApp', os: this.originalAndroidData.os || this.androidKey};
          this.androidData = Object.assign({}, this.originalAndroidData, appKeys);
        });
    }
  }
</script>

<style lang="scss" scoped>
  .v-icon {
    color: $ads-navy;
  }
</style>
